import { ArrowLeft, Presentation } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { createCatalogTransport as defaultCreateCatalogTransport } from "../catalog/catalogTransport";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { canvasPath, classPath, useNavigate } from "../router";
import { PresentationShow } from "../viewer/PresentationShow";

import type { ApiErrorCode } from "../api/client";
import type { CatalogTransportFactory } from "../catalog/catalogTransport";
import type { CanvasRecord, PresentationStudent } from "@canvaschool/contracts";

const formatTime = (value: string) =>
  new Date(value).toLocaleString("ko-KR", {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const studentLabel = (student: PresentationStudent) =>
  `${student.studentNumber}번${
    student.displayName ? ` ${student.displayName}` : ""
  }`;

type Show = { studentNumber: number; canvasId: string };

/** 교사의 발표 수업 화면: 에디터 없이 학생 카드 격자. */
export const PresentationLessonPage = ({
  classId,
  canvas,
  createCatalogTransport = defaultCreateCatalogTransport,
}: {
  classId: string;
  canvas: CanvasRecord;
  createCatalogTransport?: CatalogTransportFactory;
}) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { api } = auth;
  const navigate = useNavigate();
  const lessonId = canvas.id;

  const [students, setStudents] = useState<PresentationStudent[] | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [archived, setArchived] = useState(false);
  const [show, setShow] = useState<Show | null>(null);
  const [busyNumber, setBusyNumber] = useState<number | null>(null);
  const sequenceRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const sequence = ++sequenceRef.current;
    try {
      const list = await api.listPresentationStudents(classId, lessonId);
      if (mountedRef.current && sequence === sequenceRef.current) {
        setStudents(
          [...list.items].sort((a, b) => a.studentNumber - b.studentNumber),
        );
        setError(null);
      }
    } catch (failure) {
      if (mountedRef.current && sequence === sequenceRef.current) {
        setError(authRef.current.handleError(failure));
      }
    }
  }, [api, classId, lessonId]);

  useEffect(() => {
    void refresh();
    void api
      .getClass(classId)
      .then((record) => {
        if (mountedRef.current) {
          setArchived(record.status === "ARCHIVED");
        }
      })
      .catch(() => undefined);
  }, [api, classId, refresh]);

  useEffect(() => {
    const transport = createCatalogTransport({
      classId,
      getCsrfToken: async () => {
        const current = authRef.current;
        return current.state.status === "authenticated"
          ? current.state.session.csrfToken
          : null;
      },
      events: {
        onJoined: () => void refresh(),
        onChanged: () => void refresh(),
        onJoinFailed: (code) => {
          if (code === "UNAUTHENTICATED" || code === "ACCOUNT_DISABLED") {
            authRef.current.handleError(new ApiError(401, code));
          }
        },
        onDisconnected: () => undefined,
        onAccessLost: (code) => {
          authRef.current.handleError(new ApiError(401, code));
        },
      },
    });
    transport.connect();
    return () => transport.close();
  }, [classId, createCatalogTransport, refresh]);

  /** The student's canvas id. An existing canvas is used as listed: only
   * creating one needs the POST, which an archived class refuses. */
  const open = async (student: PresentationStudent): Promise<string | null> => {
    if (student.canvasId) {
      return student.canvasId;
    }
    setBusyNumber(student.studentNumber);
    try {
      return (
        await api.openPresentationStudentCanvas(
          classId,
          lessonId,
          student.studentNumber,
        )
      ).id;
    } catch (failure) {
      setError(authRef.current.handleError(failure));
      return null;
    } finally {
      if (mountedRef.current) {
        setBusyNumber(null);
      }
    }
  };

  const edit = async (student: PresentationStudent) => {
    const childId = await open(student);
    if (childId) {
      navigate(canvasPath(classId, childId));
    }
  };

  const present = async (student: PresentationStudent) => {
    const childId = await open(student);
    if (childId) {
      setShow({ studentNumber: student.studentNumber, canvasId: childId });
    }
  };

  const enabled = (students ?? []).filter((student) => student.enabled);
  const completed = enabled.filter((student) => student.completedAt).length;
  const nextStudent = show
    ? enabled.find((student) => student.studentNumber > show.studentNumber)
    : undefined;

  return (
    <div className="canvaschool-lesson">
      <div className="canvaschool-lesson-bar">
        <Link to={classPath(classId)} className="canvaschool-lesson-back">
          <ArrowLeft size={16} aria-hidden="true" />
          학급으로
        </Link>
        <h1 className="canvaschool-lesson-title">{canvas.title}</h1>
        {students && (
          <span className="canvaschool-lesson-progress">
            <span role="status">
              완료 {completed} / {enabled.length}
            </span>
            <span className="canvaschool-lesson-meter" aria-hidden="true">
              <span
                style={{
                  width: `${
                    enabled.length ? (completed / enabled.length) * 100 : 0
                  }%`,
                }}
              />
            </span>
          </span>
        )}
      </div>
      <div className="canvaschool-lesson-body">
        {error && (
          <div className="canvaschool-actions canvaschool-lesson-error">
            <ErrorAlert code={error} />
            <button type="button" onClick={() => void refresh()}>
              다시 시도
            </button>
          </div>
        )}
        {archived && (
          <p className="canvaschool-hint">
            보관된 학급입니다. 발표는 볼 수 있습니다.
          </p>
        )}
        {!students && !error && (
          <p className="canvaschool-hint">불러오는 중…</p>
        )}
        {students && (
          <ul className="canvaschool-lesson-grid">
            {students.map((student) => {
              const done = Boolean(student.completedAt);
              return (
                <li
                  key={student.studentNumber}
                  className={`canvaschool-lesson-card${done ? " is-done" : ""}${
                    student.enabled ? "" : " is-disabled"
                  }`}
                >
                  <div className="canvaschool-lesson-card-head">
                    <span className="canvaschool-lesson-number">
                      {student.studentNumber}번
                    </span>
                    <span className="canvaschool-lesson-name">
                      {student.displayName ?? "(이름 입력 전)"}
                    </span>
                  </div>
                  {student.enabled ? (
                    <>
                      <p
                        className={`canvaschool-badge${
                          done
                            ? " canvaschool-status--on"
                            : student.canvasId
                            ? " canvaschool-status--wait"
                            : ""
                        }`}
                      >
                        {done
                          ? "완료"
                          : student.canvasId
                          ? "작성 중"
                          : "시작 전"}
                      </p>
                      {student.canvasId && (
                        <p className="canvaschool-lesson-meta">
                          {`슬라이드 ${student.slideCount}장`}
                        </p>
                      )}
                      {student.updatedAt && (
                        <p className="canvaschool-lesson-meta">
                          마지막 수정 {formatTime(student.updatedAt)}
                        </p>
                      )}
                      <div className="canvaschool-lesson-actions">
                        <button
                          type="button"
                          className="canvaschool-btn-secondary"
                          disabled={busyNumber !== null}
                          aria-label={`${studentLabel(student)} 열기`}
                          onClick={() => void edit(student)}
                        >
                          열기
                        </button>
                        <button
                          type="button"
                          className="canvaschool-btn-primary"
                          disabled={busyNumber !== null}
                          aria-label={`${studentLabel(student)} 발표`}
                          onClick={() => void present(student)}
                        >
                          <Presentation size={16} aria-hidden="true" />
                          발표
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="canvaschool-lesson-meta">사용 중지됨</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {show && (
        <PresentationShow
          key={show.canvasId}
          classId={classId}
          canvasId={show.canvasId}
          onExit={() => setShow(null)}
          next={
            nextStudent
              ? {
                  label: `다음 학생: ${studentLabel(nextStudent)}`,
                  onNext: () => {
                    void present(nextStudent);
                  },
                }
              : undefined
          }
        />
      )}
    </div>
  );
};
